import { Icon, MenuItem } from "@mui/material";
import React from "react";
import styled from "styled-components";

import { PresentationFilters } from "common/constants";
import { ActionPermissionsObject, PermissionActionType, WorkspaceResourceType, WorkspaceUserRole, WorkspacePlanTier } from "common/interfaces";
import PresentationLibraryController, { PresentationLibraryPresentation } from "js/controllers/PresentationLibraryController";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { getPresentation } from "js/core/models/presentation";
import ExportDialog from "js/react/views/PresentationSettings/dialogs/ExportDialog";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog,
    ShowInputDialog,
    ShowUpgradeDialog
} from "js/react/components/Dialogs/BaseDialog";
import MovePresentationToWorkspaceDialog from "js/react/components/Dialogs/MovePresentationToWorkspaceDialog";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { browserHistory } from "js/react/history";
import { themeColors } from "js/react/sharedStyles";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { ShareDialog } from "js/react/views/PresentationSettings/dialogs/ShareDialog";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import ShareViewOnlyPresentationDialog from "js/react/views/PresentationSettings/dialogs/ShareViewOnlyPresentationDialog";
import CreateTeamTemplateDialog from "js/react/views/TeamResources/dialogs/CreateTeamTemplateDialog";
import { sanitizeHtmlText } from "js/core/utilities/htmlTextHelpers";
import { Icon as IconComponent } from "js/Components/Icon";

const StyledMenuItem = styled(MenuItem)`
    &&& {
        color: ${themeColors.lighterGray};
    }
`;

const logger = getLogger(LogGroup.LIBRARY);

export function getPresentationContextMenuItems({ selectedPresentations }: { selectedPresentations: PresentationLibraryPresentation[] }) {
    const currentFilter = PresentationLibraryController.filter;
    const currentFilterMetadata = PresentationLibraryController.filterMetadata;
    const isGuest = WorkspaceController.role === WorkspaceUserRole.GUEST;
    const isBasic = WorkspaceController.plan.tier === WorkspacePlanTier.BASIC;

    const canCreateResources = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCES].create;
    const canUpgradeToCreateResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE);
    const canUpgradeToMoveToAnotherWorkspace = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.EXTERNAL_WORKSPACES_RESOURCE_TRANSFER, PermissionActionType.USE);
    const canUpgradeToManageSharedResources = !isBasic && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const getFirstPresentationModel = () => {
        return getPresentation(selectedPresentations[0].id, { permission: "write", autoSync: true });
    };

    const getPresentationModels = (permission: "read" | "write" = "write") => {
        return Promise.all(selectedPresentations.map(p => getPresentation(p.id, { permission, autoSync: true })));
    };

    const handleEdit = event => {
        const openInNewTab = event.metaKey || event.shiftKey;
        AppController.showEditor({ presentationId: selectedPresentations[0].id, openInNewTab });
    };

    const handlePresent = () => {
        PresentationLibraryController.playPresentation(selectedPresentations[0].id, true);
    };

    const handleDelete = async () => {
        const ownedPresentations = selectedPresentations.filter(p => p.permissions.owner);
        const collaboratedPresentations = selectedPresentations.filter(p => !p.permissions.owner);

        if (ownedPresentations.length > 0) {
            withProgressDialog({
                title: "Moving presentations to trash...",
                action: () =>
                    withProgressDialog({
                        title: "Moving presentations to trash...",
                        action: () => PresentationLibraryController.trashPresentations(ownedPresentations.map(p => p.id))
                    })
                        .catch(err => {
                            ShowErrorDialog({
                                title: "Unable to move presentation(s) to trash",
                                message: err.message
                            });
                        })
            });
        }

        if (collaboratedPresentations.length > 0) {
            ShowConfirmationDialog({
                title: "Are you sure you want to stop collaborating on the selected presentation(s)?",
                message: "Shared presentations will be unshared with you and you will no longer be able to view or edit them unless they are reshared with you.",
                acceptCallback: () => {
                    withProgressDialog({
                        title: "Unsharing presentation(s)...",
                        action: () => PresentationLibraryController.deletePresentations(collaboratedPresentations.map(p => p.id))
                    })
                        .catch(err => {
                            ShowErrorDialog({
                                title: "Unable to unshare presentation(s)",
                                message: err.message
                            });
                        });
                }
            });
        }
    };

    const handleDeletePermanently = () => {
        ShowConfirmationDialog({
            title: "Are you sure you want to permanently delete this presentation?",
            message: "You can't undo this action.",
            acceptCallback: () => {
                withProgressDialog({
                    title: "Deleting presentation(s)...",
                    action: () => PresentationLibraryController.deletePresentations(selectedPresentations.map(p => p.id))
                })
                    .catch(err => {
                        ShowErrorDialog({
                            title: "Unable to delete presentation(s)",
                            message: err.message
                        });
                    });
            }
        });
    };

    const handleMoveWorkspaces = () => {
        ShowDialog(MovePresentationToWorkspaceDialog, {
            presentations: selectedPresentations,
            onClose: () => {
                PresentationLibraryController.forceRefreshPresentations(selectedPresentations.map(p => p.id));
            }
        });
    };

    const handleRecoverFromTrash = () => {
        PresentationLibraryController.recoverPresentations(selectedPresentations.map(p => p.id))
            .catch(err => {
                ShowErrorDialog({
                    title: "Unable to recover presentation(s)",
                    message: err.message
                });
            });
    };

    const handleShareViewOnlyPresentation = () => {
        getPresentationModels("read")
            .then(presentations => {
                ShowDialog(ShareViewOnlyPresentationDialog, { presentation: presentations[0], onClose: () => presentations.forEach(p => p.disconnect()) });
            })
            .catch(err => {
                ShowErrorDialog({
                    title: "Unable to share presentation(s)",
                    message: err.message
                });
            });
    };

    const handleSendPresentation = () => {
        getPresentationModels()
            .then(presentations => {
                ShowDialog(ShareDialog, { presentation: presentations[0], onClose: () => presentations.forEach(p => p.disconnect()) });
            })
            .catch(err => {
                ShowErrorDialog({
                    title: "Unable to share presentation(s)",
                    message: err.message
                });
            });
    };

    const handleSharePresentation = () => {
        getPresentationModels()
            .then(presentations => {
                ShowDialog(ShareDialog, { presentation: presentations[0], selectedPanel: "collaborate", onClose: () => presentations.forEach(p => p.disconnect()) });
            })
            .catch(err => {
                ShowErrorDialog({
                    title: "Unable to share presentation(s)",
                    message: err.message
                });
            });
    };

    const handleRemoveFromFolder = () => {
        const folderId = currentFilter.subFolderId ?? currentFilter.folderId;
        const resourceFolder = WorkspaceController.resourceFolders.find(rf => rf.id === folderId);

        withProgressDialog({
            title: `Removing presentation from ${resourceFolder.name}...`,
            action: async () => {
                try {
                    const resources = selectedPresentations.map(p => ({
                        resourceId: p.id,
                        resourceType: WorkspaceResourceType.PRESENTATION
                    }));
                    await WorkspaceController.updateWorkspaceResourceFolderResources({
                        folderId,
                        update: {
                            remove: resources
                        }
                    });
                } catch (err) {
                    logger.error(err, "[PresentationContextMenu] handleRemoveFromFolder() failed");

                    ShowErrorDialog({
                        title: "Unable to remove presentation(s)",
                        message: err.message
                    });
                }
            }
        });
    };

    const handleConvertToTemplate = async () => {
        if (canUpgradeToCreateResources) {
            ShowDialog(GuestWorkspaceDialog);
            return;
        } else if (canUpgradeToManageSharedResources) {
            ShowUpgradeDialog({
                type: UpgradePlanDialogType.UPGRADE_PLAN_TO_TEAM,
                analytics: { cta: "TeamTemplates" },
                workspaceId: WorkspaceController.workspaceId
            });
            return;
        }
        const existingTags = [];
        const presentation = await getFirstPresentationModel();
        const name = presentation.get("name") || "";
        const description = presentation.get("description") || "";
        const tags = presentation.get("tags") || [];
        presentation.disconnect();

        ShowDialog(CreateTeamTemplateDialog, {
            title: "Create a template from this presentation?",
            subTitle: "Publish a copy of this presentation for your team to start from—or save it as a draft and publish it later. View and edit this template in Team Resources at any time.",
            name,
            description,
            tags,
            existingTags,
            callback: async ({
                isPublished,
                name,
                description,
                tags,
            }) => {
                const duplicatedPresentation = await withProgressDialog({
                    title: "Duplicating presentation...",
                    action: () => PresentationLibraryController.duplicatePresentation({ presentationId: presentation.id })
                });

                await PresentationLibraryController.convertToPresentationTemplate(
                    duplicatedPresentation,
                    {
                        tags,
                        metadata: {
                            isTemplateConfirmed: true,
                            isPublished,
                            name,
                            description,
                        }
                    },
                    true
                );
            }
        });
    };

    const handleViewProperties = async () => {
        const presentation = await getFirstPresentationModel();
        browserHistory.push(`/${presentation.id}/settings/properties`);
    };

    const handleExportPresentation = () => {
        browserHistory.push(`/${selectedPresentations[0].id}/settings/export?openDialog=true`);
    };

    const handleRenamePresentation = async () => {
        const presentationId = selectedPresentations[0].id;

        const presentationName = await ShowInputDialog({
            title: "Enter a name for your presentation",
            value: selectedPresentations[0].name,
            trimInput: true,
            onChange: str => sanitizeHtmlText(str)
        });

        if (!presentationName) {
            return;
        }

        await PresentationLibraryController.markPresentationsDirty([presentationId]);
        try {
            const presentation = await getPresentation(presentationId, { permission: "write", autoSync: false });
            try {
                await presentation.rename(presentationName);
            } finally {
                await presentation.disconnect();
            }
        } catch (err) {
            logger.error(err, "[PresentationContextMenu] handleRenamePresentation failed");
            ShowErrorDialog({ title: "Unable to rename presentation", message: err?.message ?? String(err) });
        } finally {
            await PresentationLibraryController.forceRefreshPresentations([presentationId]);
        }
    };

    const MenuItems = [];

    const renderMoveToAnotherWorkspace = (isOwner: boolean, includeDivider = false) => {
        if (AppController.workspacesMetadata.length === 1) {
            // Nowhere to move to
            return;
        }

        if (!isOwner) {
            if (canUpgradeToMoveToAnotherWorkspace) {
                // Show guest CTA regardless of them being owner or not
                MenuItems.push(
                    <StyledMenuItem
                        key="move_workspace"
                        divider={includeDivider}
                        onClick={() => ShowDialog(GuestWorkspaceDialog)}
                    >
                        <UpgradeIcon style={{ marginRight: 6 }} />
                        Move To Another Workspace
                    </StyledMenuItem>);
            }

            return;
        }

        if (currentFilter.type !== PresentationFilters.TEAM_FOLDER || currentFilterMetadata.permissions.write.object) {
            if (
                WorkspaceController.actionPermissions[ActionPermissionsObject.EXTERNAL_WORKSPACES_RESOURCE_TRANSFER].use ||
                // Special case per CS request...
                canUpgradeToMoveToAnotherWorkspace
            ) {
                MenuItems.push(
                    <MenuItem
                        key="move_workspace"
                        divider={includeDivider}
                        onClick={handleMoveWorkspaces}
                    >
                        <Icon>business</Icon>Move To Another Workspace
                    </MenuItem>);
            }
        }
    };

    if (selectedPresentations.length === 1) {
        const presentation = selectedPresentations[0];

        if (currentFilter.type === PresentationFilters.TRASH) {
            MenuItems.push(
                <MenuItem key="recover" onClick={handleRecoverFromTrash}>
                    <Icon>restore_from_trash</Icon>
                    Recover From Trash
                </MenuItem>
            );
            MenuItems.push(
                <MenuItem key="delete-permanently" onClick={handleDeletePermanently}>
                    <Icon>delete_forever</Icon>Delete Permanently
                </MenuItem>
            );
        } else {
            MenuItems.push(
                <MenuItem key="present-all" onClick={handlePresent}>
                    <Icon>present_to_all</Icon>Present
                </MenuItem>
            );

            if (presentation.permissions.owner || presentation.permissions.write) {
                {
                    (canCreateResources || isGuest) &&
                        MenuItems.push(
                            <MenuItem key="edit" onClick={handleEdit}>
                                <Icon>edit</Icon>Edit
                            </MenuItem>
                        );
                }
                {
                    canUpgradeToCreateResources && !isGuest &&
                        MenuItems.push(
                            <StyledMenuItem key="edit" onClick={() => ShowDialog(ChangePlanDialog, { featuredPlan: WorkspacePlanTier.PRO })}>
                                <UpgradeIcon style={{ marginRight: 6 }} />
                                Edit
                            </StyledMenuItem>
                        );
                }

                MenuItems.push(<hr key="divider2" />);

                MenuItems.push(
                    <MenuItem key="send" onClick={handleSendPresentation}>
                        <Icon>share</Icon>Share...
                    </MenuItem>
                );
                {
                    (canCreateResources || isGuest) &&
                        MenuItems.push(
                            <MenuItem key="share" divider onClick={handleSharePresentation}>
                                <Icon>person_add_alt_1</Icon>Invite Collaborators...
                            </MenuItem>
                        );
                }

                {
                    canUpgradeToCreateResources && !isGuest &&
                        MenuItems.push(
                            <StyledMenuItem key="share" divider onClick={() => ShowDialog(ChangePlanDialog,
                                {
                                    featuredPlan: WorkspacePlanTier.PRO,
                                    analytics: { cta: "invite_collaborators" }
                                })}>
                                <UpgradeIcon style={{ marginRight: 6 }} />
                                Invite Collaborators...
                            </StyledMenuItem>
                        );
                }

                {
                    (canCreateResources || isGuest) &&
                        MenuItems.push(
                            <MenuItem key="rename" onClick={() => handleRenamePresentation()}>
                                <IconComponent large color="#666666" style={{
                                    marginRight: "6px"
                                }}>edit_square</IconComponent>Rename...
                            </MenuItem>
                        );
                }
                {
                    (canCreateResources || isGuest) &&
                        MenuItems.push(
                            <MenuItem key="properties" onClick={handleViewProperties}>
                                <Icon>info</Icon>View Properties...
                            </MenuItem>
                        );
                }
                {
                    WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION].use && !isGuest &&
                    MenuItems.push(
                        <MenuItem key="share" divider onClick={()=>handleExportPresentation()}>
                            <Icon>cloud_download</Icon>Export...
                        </MenuItem>
                    );
                }

                {
                    canUpgradeToCreateResources && !isGuest &&
                        MenuItems.push(
                            <StyledMenuItem key="properties" onClick={handleViewProperties}>
                                <UpgradeIcon style={{ marginRight: 6 }} />
                                View Properties...
                            </StyledMenuItem>
                        );
                }
            } else {
                MenuItems.push(
                    <MenuItem divider key="send" onClick={handleShareViewOnlyPresentation}>
                        <Icon>share</Icon>Share...
                    </MenuItem>
                );
            }

            if ((currentFilter.type !== PresentationFilters.TEAM_FOLDER || currentFilterMetadata.permissions.write.object) && canCreateResources) {
                MenuItems.push(
                    <MenuItem key="duplicate" onClick={() => PresentationLibraryController.handleDuplicate(selectedPresentations[0].id)}>
                        <Icon>content_copy</Icon>Duplicate
                    </MenuItem>
                );
            }
            if (canUpgradeToCreateResources) {
                MenuItems.push(
                    <StyledMenuItem key="duplicate" onClick={() => {
                        if (isGuest) {
                            ShowDialog(GuestWorkspaceDialog);
                        } else {
                            PresentationLibraryController.handleDuplicate(selectedPresentations[0].id);
                        }
                    }}>
                        <UpgradeIcon style={{ marginRight: 6 }} />
                        Duplicate
                    </StyledMenuItem>
                );
            }

            if (presentation.permissions.owner || presentation.permissions.write) {
                {
                    WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage &&
                        MenuItems.push(
                            <MenuItem key="convert-to-template" onClick={handleConvertToTemplate}>
                                <Icon>file_copy</Icon>Create Team Template...
                            </MenuItem>
                        );
                }
                {
                    (canUpgradeToCreateResources || canUpgradeToManageSharedResources) &&
                        MenuItems.push(
                            <StyledMenuItem key="convert-to-template" onClick={() => {
                                if (isGuest) {
                                    ShowDialog(GuestWorkspaceDialog);
                                } else {
                                    ShowDialog(ChangePlanDialog,
                                        {
                                            featuredPlan: WorkspacePlanTier.TEAM,
                                            analytics: { cta: "create_team_template" }
                                        });
                                }
                            }}>
                                <UpgradeIcon style={{ marginRight: 6 }} />
                                Create Team Template...
                            </StyledMenuItem>
                        );
                }
            }

            renderMoveToAnotherWorkspace(presentation.permissions.owner, false);

            MenuItems.push(<hr key="divider4" />);

            if (currentFilter.type === PresentationFilters.FOLDER) {
                MenuItems.push(
                    <MenuItem key="remove" divider onClick={handleRemoveFromFolder}>
                        <Icon>remove_circle</Icon>Remove from Folder
                    </MenuItem>
                );
            }
            if (currentFilter.type === PresentationFilters.TEAM_FOLDER) {
                if (currentFilterMetadata.permissions.write.object) {
                    MenuItems.push(
                        <MenuItem key="remove" divider onClick={handleRemoveFromFolder}>
                            <Icon>remove_circle</Icon>Remove from {currentFilter.subFolderId ? "Subfolder" : "Team Folder"}
                        </MenuItem>
                    );
                }
            }

            if (presentation.permissions.owner) {
                MenuItems.push(
                    <MenuItem key="trash" onClick={handleDelete}>
                        <Icon>delete</Icon>Move To Trash
                    </MenuItem>
                );
            } else {
                MenuItems.push(
                    <MenuItem key="unshare" onClick={handleDelete}>
                        <Icon>delete</Icon>Unshare with me
                    </MenuItem>
                );
            }
        }
    } else {
        if (currentFilter.type === PresentationFilters.TRASH) {
            MenuItems.push(
                <MenuItem key="recover"
                    onClick={handleRecoverFromTrash}>
                    <Icon>restore_from_trash</Icon>
                    Recover From Trash
                </MenuItem>
            );
            MenuItems.push(
                <MenuItem key="delete-permanently"
                    onClick={handleDeletePermanently}>
                    <Icon>delete_forever</Icon>
                    Delete Permanently
                </MenuItem>
            );
        } else {
            if (currentFilter.type === PresentationFilters.FOLDER) {
                MenuItems.push(
                    <MenuItem key="remove"
                        onClick={handleRemoveFromFolder}>
                        <Icon>remove_circle</Icon>
                        Remove from Folder
                    </MenuItem>
                );
            }

            let hasShared = false;
            let hasOwned = false;
            for (let presentation of selectedPresentations) {
                if (presentation.permissions.owner) {
                    hasOwned = true;
                } else {
                    hasShared = true;
                }
            }

            renderMoveToAnotherWorkspace(hasOwned, true);

            let label;
            if (hasOwned && hasShared) {
                label = "Move to Trash or Unshare";
            } else if (hasOwned) {
                label = "Move to Trash";
            } else {
                label = "Unshare with me";
            }

            MenuItems.push(
                <MenuItem key="trash"
                    onClick={handleDelete}>
                    <Icon>delete</Icon>
                    {label}
                </MenuItem>
            );
        }
    }

    return MenuItems;
}
